// ============================================================================
// HCAI Reporting Tool - Comprehensive HCAI Surveillance Form
// Official healthcare-associated infection case surveillance form
// Implements the Department of Health HCAI Surveillance Form
// ============================================================================

import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useEffect } from 'react';
import { hcaiSurveillanceFormSchema, HCAISurveillanceFormData, getTodayString } from '@/utils/validation';
import { saveHCAICase } from '@/services/firestore.service';
import { useAuth } from '@/contexts/AuthContext';

interface HCAISurveillanceFormProps {
  onSuccess?: () => void;
}

const DEVICE_OPTIONS = [
  { value: 'indwellingUrinaryCatheter', label: 'Indwelling urinary catheter' },
  { value: 'mechanicalVentilation', label: 'Mechanical Ventilation' },
  { value: 'tracheostomy', label: 'Tracheostomy' },
  { value: 'centralVenousCatheter', label: 'Central Venous Catheter' },
  { value: 'arterialLine', label: 'Arterial Line' },
  { value: 'peripheralVenousLine', label: 'Peripheral Venous Line' },
  { value: 'otherDrainageCatheters', label: 'Other drainage catheters' },
] as const;

const INFECTION_TYPES = [
  { value: 'ssi', label: 'Surgical Site Infection (SSI)' },
  { value: 'uti', label: 'Catheter-Associated Urinary Tract Infection (CAUTI)' },
  { value: 'pneumonia', label: 'Pneumonia' },
  { value: 'bsi', label: 'Blood Stream Infection (BSI)' },
  { value: 'sepsis', label: 'Clinical Sepsis' },
  { value: 'other', label: 'Others' },
] as const;

export function HCAISurveillanceForm({ onSuccess }: HCAISurveillanceFormProps) {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState<'identification' | 'clinical' | 'outcome' | 'reporting'>('identification');

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    control,
    setValue
  } = useForm<HCAISurveillanceFormData>({
    resolver: zodResolver(hcaiSurveillanceFormSchema),
    mode: 'onBlur',
    defaultValues: {
      district: '',
      subDistrict: '',
      hospital: '',
      department: '',
      patientName: '',
      hospitalNumber: '',
      transferredIn: false,
      transferringFacility: '',
      gender: undefined,
      age: undefined,
      ward: '',
      admissionDate: getTodayString(),
      clinicalDiagnosis: '',
      riskFactors: {
        underlyingDisease: false,
        immunosuppressiveTherapy: false,
        prolongedHospitalization: false,
        prematurity: false,
        surgicalProcedure: false,
        others: false,
      },
      devices: [],
      antibiotics: [],
      microbiological: [],
      infections: [],
      patientOutcome: 'admitted',
      reportedBy: '',
      reporterRank: '',
      reportDate: '',
    }
  });

  const { fields: deviceFields, append: appendDevice, remove: removeDevice } = useFieldArray({
    control,
    name: 'devices',
  });

  const { fields: antibioticFields, append: appendAntibiotic, remove: removeAntibiotic } = useFieldArray({
    control,
    name: 'antibiotics',
  });

  const { fields: microFields, append: appendMicro, remove: removeMicro } = useFieldArray({
    control,
    name: 'microbiological',
  });

  const { fields: infectionFields, append: appendInfection, remove: removeInfection } = useFieldArray({
    control,
    name: 'infections',
  });

  const watchRiskFactors = watch('riskFactors');
  const watchInfectionType = watch('infections');
  const watchTransferredIn = watch('transferredIn');

  useEffect(() => {
    if (!watchTransferredIn) {
      setValue('transferringFacility', '');
    }
  }, [watchTransferredIn, setValue]);

  // Calculate section completion status
  const getSectionStatus = (section: string): 'complete' | 'incomplete' | 'current' => {
    if (section === activeSection) return 'current';
    if (section === 'identification') {
      const hasData = watch('district') || watch('hospital') || watch('patientName');
      return hasData ? 'complete' : 'incomplete';
    }
    if (section === 'clinical') {
      const hasData = watch('clinicalDiagnosis') || deviceFields.length > 0 || antibioticFields.length > 0;
      return hasData ? 'complete' : 'incomplete';
    }
    if (section === 'outcome') {
      const hasData = infectionFields.length > 0 || watch('patientOutcome');
      return hasData ? 'complete' : 'incomplete';
    }
    if (section === 'reporting') {
      const hasData = watch('reportedBy') || watch('reportDate');
      return hasData ? 'complete' : 'incomplete';
    }
    return 'incomplete';
  };

  // Navigation helpers
  const sections: Array<typeof activeSection> = ['identification', 'clinical', 'outcome', 'reporting'];
  const currentSectionIndex = sections.indexOf(activeSection);

  const goToNextSection = () => {
    if (currentSectionIndex < sections.length - 1) {
      setActiveSection(sections[currentSectionIndex + 1]);
    }
  };

  const goToPreviousSection = () => {
    if (currentSectionIndex > 0) {
      setActiveSection(sections[currentSectionIndex - 1]);
    }
  };

  const onSubmit = async (data: HCAISurveillanceFormData) => {
    console.log('Submit button clicked');
    console.log('Form data:', data);
    console.log('Form errors:', errors);
    console.log('Is valid:', isValid);

    if (!currentUser) {
      setError('You must be logged in to submit data');
      console.log('No current user');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      console.log('Saving HCAI case...');
      await saveHCAICase({
        ...data,
        createdBy: currentUser.uid,
        createdAt: getTodayString(),
      });

      console.log('Case saved successfully');
      setSuccess(true);
      onSuccess?.();

      setTimeout(() => {
        setSuccess(false);
        setActiveSection('identification');
        reset({
          district: '',
          subDistrict: '',
          hospital: '',
          department: '',
          patientName: '',
          hospitalNumber: '',
          transferredIn: false,
          transferringFacility: '',
          gender: undefined,
          age: undefined,
          ward: '',
          admissionDate: getTodayString(),
          clinicalDiagnosis: '',
          riskFactors: {
            underlyingDisease: false,
            immunosuppressiveTherapy: false,
            prolongedHospitalization: false,
            prematurity: false,
            surgicalProcedure: false,
            others: false,
          },
          devices: [],
          antibiotics: [],
          microbiological: [],
          infections: [],
          patientOutcome: 'admitted',
          reportedBy: '',
          reporterRank: '',
          reportDate: '',
        });
      }, 2000);
    } catch (err: any) {
      console.error('Error saving case:', err);
      setError(err.message || 'Failed to save HCAI case');
    } finally {
      setLoading(false);
    }
  };

  const SectionButton = ({ section, label }: { section: typeof activeSection; label: string }) => {
    const status = getSectionStatus(section);

    return (
      <button
        type="button"
        onClick={() => setActiveSection(section)}
        className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
          status === 'current'
            ? 'bg-primary-600 text-white'
            : status === 'complete'
            ? 'bg-success-100 text-success-800 border border-success-300'
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        disabled={loading}
      >
        <span>{label}</span>
        {status === 'complete' && <span className="text-lg">✓</span>}
      </button>
    );
  };

  return (
    <div className="card">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">HCAI Surveillance Form</h2>
        <p className="text-sm text-gray-600">
          Healthcare-Associated Infection Case Surveillance and Reporting (Department of Health)
        </p>
      </div>

      {success && (
        <div className="bg-success-50 border border-success-200 text-success-700 px-4 py-3 rounded-lg mb-4">
          ✓ HCAI case recorded successfully!
        </div>
      )}

      {error && (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Progress Indicator */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Step {['identification', 'clinical', 'outcome', 'reporting'].indexOf(activeSection) + 1} of 4
          </span>
          <span className="text-xs text-gray-500">
            {['identification', 'clinical', 'outcome', 'reporting'].filter((s) => getSectionStatus(s) === 'complete').length} sections completed
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{
              width: `${(((['identification', 'clinical', 'outcome', 'reporting'].indexOf(activeSection) + 1) / 4) * 100)}%`,
            }}
          />
        </div>
      </div>

      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 pb-4 border-b border-gray-200">
        <SectionButton section="identification" label="Case ID" />
        <SectionButton section="clinical" label="Clinical & Laboratory Data" />
        <SectionButton section="outcome" label="Outcome" />
        <SectionButton section="reporting" label="Reporting" />
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* CASE IDENTIFICATION DATA */}
        {activeSection === 'identification' && (
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Case Identification Data</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="district" className="label">
                  Name of District <span className="text-danger-600">*</span>
                </label>
                <input
                  id="district"
                  type="text"
                  {...register('district')}
                  className="input"
                  disabled={loading}
                />
                {errors.district && <p className="text-sm text-danger-600 mt-1">{errors.district.message}</p>}
              </div>

              <div>
                <label htmlFor="subDistrict" className="label">
                  Name of Sub-District
                </label>
                <input
                  id="subDistrict"
                  type="text"
                  {...register('subDistrict')}
                  className="input"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="hospital" className="label">
                  Name of Hospital <span className="text-danger-600">*</span>
                </label>
                <input
                  id="hospital"
                  type="text"
                  {...register('hospital')}
                  className="input"
                  disabled={loading}
                />
                {errors.hospital && <p className="text-sm text-danger-600 mt-1">{errors.hospital.message}</p>}
              </div>

              <div>
                <label htmlFor="department" className="label">
                  Name of Department <span className="text-danger-600">*</span>
                </label>
                <input
                  id="department"
                  type="text"
                  {...register('department')}
                  className="input"
                  disabled={loading}
                />
                {errors.department && <p className="text-sm text-danger-600 mt-1">{errors.department.message}</p>}
              </div>

              <div>
                <label htmlFor="patientName" className="label">
                  Name of Patient <span className="text-danger-600">*</span>
                </label>
                <input
                  id="patientName"
                  type="text"
                  {...register('patientName')}
                  className="input"
                  disabled={loading}
                />
                {errors.patientName && <p className="text-sm text-danger-600 mt-1">{errors.patientName.message}</p>}
              </div>

              <div>
                <label htmlFor="hospitalNumber" className="label">
                  Hospital No
                </label>
                <input
                  id="hospitalNumber"
                  type="text"
                  {...register('hospitalNumber')}
                  className="input"
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="ward" className="label">
                  Ward <span className="text-danger-600">*</span>
                </label>
                <input
                  id="ward"
                  type="text"
                  placeholder="e.g., ICU, Ward A, Emergency Department"
                  {...register('ward')}
                  className="input"
                  disabled={loading}
                />
                {errors.ward && <p className="text-sm text-danger-600 mt-1">{errors.ward.message}</p>}
              </div>

              <div>
                <label htmlFor="admissionDate" className="label">
                  Date of Admission <span className="text-danger-600">*</span>
                </label>
                <input
                  id="admissionDate"
                  type="date"
                  {...register('admissionDate')}
                  className="input"
                  disabled={loading}
                />
                {errors.admissionDate && <p className="text-sm text-danger-600 mt-1">{errors.admissionDate.message}</p>}
              </div>
            </div>

            {/* Transferred In */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="mb-4">
                <label className="label">
                  Transferred In
                </label>
                <Controller
                  name="transferredIn"
                  control={control}
                  render={({ field }) => (
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={field.value === true}
                          onChange={() => field.onChange(true)}
                          disabled={loading}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">Yes</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          checked={field.value === false || field.value === undefined}
                          onChange={() => field.onChange(false)}
                          disabled={loading}
                          className="w-4 h-4"
                        />
                        <span className="text-sm">No</span>
                      </label>
                    </div>
                  )}
                />
              </div>

              {watchTransferredIn && (
                <div>
                  <label htmlFor="transferringFacility" className="label">
                    Transferring Facility <span className="text-danger-600">*</span>
                  </label>
                  <input
                    id="transferringFacility"
                    type="text"
                    {...register('transferringFacility')}
                    className="input"
                    disabled={loading}
                  />
                  {errors.transferringFacility && (
                    <p className="text-sm text-danger-600 mt-1">{errors.transferringFacility.message}</p>
                  )}
                </div>
              )}
            </div>

            {/* Gender and Age */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Gender</label>
                <div className="flex gap-4 mt-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="M"
                      {...register('gender')}
                      disabled={loading}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Male</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      value="F"
                      {...register('gender')}
                      disabled={loading}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">Female</span>
                  </label>
                </div>
              </div>

              <div>
                <label htmlFor="age" className="label">Age</label>
                <input
                  id="age"
                  type="number"
                  {...register('age', { valueAsNumber: true })}
                  className="input"
                  disabled={loading}
                  min="0"
                  max="150"
                />
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={goToPreviousSection}
                disabled={currentSectionIndex === 0 || loading}
                className="btn btn-secondary flex-1"
              >
                ← Previous
              </button>
              <button
                type="button"
                onClick={goToNextSection}
                disabled={currentSectionIndex === sections.length - 1 || loading}
                className="btn btn-primary flex-1"
              >
                Next →
              </button>
            </div>
          </section>
        )}

        {/* CLINICAL DATA */}
        {activeSection === 'clinical' && (
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Clinical & Laboratory Data</h3>

            {/* Clinical Diagnosis */}
            <div>
              <label htmlFor="clinicalDiagnosis" className="label">
                Clinical Diagnosis
              </label>
              <textarea
                id="clinicalDiagnosis"
                {...register('clinicalDiagnosis')}
                className="input"
                disabled={loading}
                rows={2}
                placeholder="e.g., Sepsis, Pneumonia, etc."
              />
              {errors.clinicalDiagnosis && (
                <p className="text-sm text-danger-600 mt-1">{errors.clinicalDiagnosis.message}</p>
              )}
            </div>

            {/* Risk Factors */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-4">General Predisposing Risk Factors</h4>

              <div className="space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('riskFactors.underlyingDisease')}
                    disabled={loading}
                    className="w-4 h-4 mt-1"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Underlying Disease</span>
                    <span className="text-gray-600"> (e.g., Diabetes Mellitus, Malignancy, Kidney Disease)</span>
                  </span>
                </label>

                {watchRiskFactors?.underlyingDisease && (
                  <div className="ml-7">
                    <input
                      type="text"
                      placeholder="Specify..."
                      {...register('riskFactors.underlyingDiseaseSpec')}
                      className="input text-sm"
                      disabled={loading}
                    />
                  </div>
                )}

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('riskFactors.immunosuppressiveTherapy')}
                    disabled={loading}
                    className="w-4 h-4 mt-1"
                  />
                  <span className="text-sm font-medium">Immunosuppressive Therapy</span>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('riskFactors.prolongedHospitalization')}
                    disabled={loading}
                    className="w-4 h-4 mt-1"
                  />
                  <span className="text-sm font-medium">Prolonged Hospitalization (&gt; 2 weeks)</span>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('riskFactors.prematurity')}
                    disabled={loading}
                    className="w-4 h-4 mt-1"
                  />
                  <span className="text-sm font-medium">Prematurity/Low Birth Weight</span>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('riskFactors.surgicalProcedure')}
                    disabled={loading}
                    className="w-4 h-4 mt-1"
                  />
                  <span className="text-sm font-medium">Surgical Procedure – 1 month/1 year (with implant)</span>
                </label>

                {watchRiskFactors?.surgicalProcedure && (
                  <div className="ml-7">
                    <input
                      type="text"
                      placeholder="Specify procedure type..."
                      {...register('riskFactors.surgicalProcedureType')}
                      className="input text-sm"
                      disabled={loading}
                    />
                  </div>
                )}

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    {...register('riskFactors.others')}
                    disabled={loading}
                    className="w-4 h-4 mt-1"
                  />
                  <span className="text-sm font-medium">Others</span>
                </label>

                {watchRiskFactors?.others && (
                  <div className="ml-7">
                    <input
                      type="text"
                      placeholder="Specify..."
                      {...register('riskFactors.othersSpec')}
                      className="input text-sm"
                      disabled={loading}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Devices */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Types of Devices Used Before Onset of HCAI</h4>
                <button
                  type="button"
                  onClick={() => appendDevice({ deviceType: 'indwellingUrinaryCatheter', insertionDate: '' })}
                  className="btn btn-sm btn-primary"
                  disabled={loading}
                >
                  + Add Device
                </button>
              </div>

              {deviceFields.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No devices added yet.</p>
              ) : (
                <div className="space-y-3">
                  {deviceFields.map((field, index) => (
                    <div key={field.id} className="bg-gray-50 rounded p-3 border border-gray-200">
                      <div className="flex justify-between items-start mb-3">
                        <label className="label text-sm font-medium">Device Type</label>
                        <button
                          type="button"
                          onClick={() => removeDevice(index)}
                          className="text-danger-600 text-xs font-medium"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select
                          {...register(`devices.${index}.deviceType`)}
                          className="input text-sm"
                          disabled={loading}
                        >
                          {DEVICE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>

                        <div>
                          <label className="label text-xs">Date of Insertion</label>
                          <input
                            type="date"
                            {...register(`devices.${index}.insertionDate`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Date of Removal</label>
                          <input
                            type="date"
                            {...register(`devices.${index}.removalDate`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Antibiotic Therapy */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Antibiotic Therapy (Past 2 Weeks)</h4>
                <button
                  type="button"
                  onClick={() => appendAntibiotic({
                    name: '',
                    group: '',
                    indication: 'therapeutic',
                    dateCommenced: getTodayString(),
                    routeOfAdmission: '',
                    duration: 0,
                  })}
                  className="btn btn-sm btn-primary"
                  disabled={loading}
                >
                  + Add Antibiotic
                </button>
              </div>

              {antibioticFields.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No antibiotics recorded yet.</p>
              ) : (
                <div className="space-y-3">
                  {antibioticFields.map((field, index) => (
                    <div key={field.id} className="bg-gray-50 rounded p-3 border border-gray-200">
                      <div className="flex justify-between mb-3">
                        <label className="label text-sm font-medium">Antibiotic #{index + 1}</label>
                        <button
                          type="button"
                          onClick={() => removeAntibiotic(index)}
                          className="text-danger-600 text-xs font-medium"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                        <div>
                          <label className="label text-xs">Name and Group <span className="text-danger-600">*</span></label>
                          <input
                            type="text"
                            placeholder="e.g., Amoxicillin (Penicillin)"
                            {...register(`antibiotics.${index}.name`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Indication <span className="text-danger-600">*</span></label>
                          <select
                            {...register(`antibiotics.${index}.indication`)}
                            className="input text-sm"
                            disabled={loading}
                          >
                            <option value="empirical">Empirical</option>
                            <option value="therapeutic">Therapeutic</option>
                            <option value="prophylaxis">Prophylaxis</option>
                          </select>
                        </div>

                        <div>
                          <label className="label text-xs">Date Commenced <span className="text-danger-600">*</span></label>
                          <input
                            type="date"
                            {...register(`antibiotics.${index}.dateCommenced`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="label text-xs">Route of Admission</label>
                          <input
                            type="text"
                            placeholder="e.g., IV, Oral, IM"
                            {...register(`antibiotics.${index}.routeOfAdmission`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Duration (days)</label>
                          <input
                            type="number"
                            {...register(`antibiotics.${index}.duration`, { valueAsNumber: true })}
                            className="input text-sm"
                            disabled={loading}
                            min="0"
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Meets Local Policy</label>
                          <select
                            {...register(`antibiotics.${index}.meetsLocalPolicy`)}
                            className="input text-sm"
                            disabled={loading}
                          >
                            <option value="">Select...</option>
                            <option value="Y">Yes</option>
                            <option value="N">No</option>
                            <option value="NK">Not Known</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Microbiological Data */}
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Microbiological Data</h4>
                <button
                  type="button"
                  onClick={() => appendMicro({
                    collectionDate: getTodayString(),
                    specimenType: '',
                  })}
                  className="btn btn-sm btn-primary"
                  disabled={loading}
                >
                  + Add Record
                </button>
              </div>

              {microFields.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No microbiological data recorded yet. (Please attach relevant positive C&S report)</p>
              ) : (
                <div className="space-y-3">
                  {microFields.map((field, index) => (
                    <div key={field.id} className="bg-gray-50 rounded p-3 border border-gray-200">
                      <div className="flex justify-between mb-3">
                        <label className="label text-sm font-medium">Record #{index + 1}</label>
                        <button
                          type="button"
                          onClick={() => removeMicro(index)}
                          className="text-danger-600 text-xs font-medium"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="label text-xs">Date of Collection <span className="text-danger-600">*</span></label>
                          <input
                            type="date"
                            {...register(`microbiological.${index}.collectionDate`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Date of Lab Report</label>
                          <input
                            type="date"
                            {...register(`microbiological.${index}.labReportDate`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Type of Specimen <span className="text-danger-600">*</span></label>
                          <input
                            type="text"
                            placeholder="e.g., Blood, Urine, Sputum"
                            {...register(`microbiological.${index}.specimenType`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>

                        <div>
                          <label className="label text-xs">Organism Isolated</label>
                          <input
                            type="text"
                            placeholder="e.g., E. coli, Staphylococcus"
                            {...register(`microbiological.${index}.organismIsolated`)}
                            className="input text-sm"
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={goToPreviousSection}
                disabled={currentSectionIndex === 0 || loading}
                className="btn btn-secondary flex-1"
              >
                ← Previous
              </button>
              <button
                type="button"
                onClick={goToNextSection}
                disabled={currentSectionIndex === sections.length - 1 || loading}
                className="btn btn-primary flex-1"
              >
                Next →
              </button>
            </div>
          </section>
        )}

        {/* OUTCOME DATA */}
        {activeSection === 'outcome' && (
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Outcome Data</h3>

            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Type of Infection Detected</h4>
                <button
                  type="button"
                  onClick={() => appendInfection({
                    infectionType: '',
                    detectionMethod: '',
                    onsetDate: '',
                  })}
                  className="btn btn-sm btn-primary"
                  disabled={loading}
                >
                  + Record Infection
                </button>
              </div>

              {infectionFields.length === 0 ? (
                <p className="text-sm text-gray-500 italic">No infections recorded.</p>
              ) : (
                <div className="space-y-4">
                  {infectionFields.map((field, index) => {
                    const infectionType = watchInfectionType?.[index]?.infectionType;

                    return (
                      <div key={field.id} className="bg-gray-50 rounded p-4 border border-gray-200">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <label className="label text-sm font-medium">Infection Type <span className="text-danger-600">*</span></label>
                            <select
                              {...register(`infections.${index}.infectionType`)}
                              className="input text-sm"
                              disabled={loading}
                            >
                              <option value="">Select infection type...</option>
                              {INFECTION_TYPES.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeInfection(index)}
                            className="text-danger-600 text-xs font-medium ml-3 mt-6"
                            disabled={loading}
                          >
                            Remove
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="label text-xs">Detection Method</label>
                            <select
                              {...register(`infections.${index}.detectionMethod`)}
                              className="input text-sm"
                              disabled={loading}
                            >
                              <option value="">Select...</option>
                              <option value="clinical">Clinical</option>
                              <option value="laboratory">Laboratory</option>
                            </select>
                          </div>

                          <div>
                            <label className="label text-xs">Date of Onset</label>
                            <input
                              type="date"
                              {...register(`infections.${index}.onsetDate`)}
                              className="input text-sm"
                              disabled={loading}
                            />
                          </div>
                        </div>

                        {/* SSI Specific Fields */}
                        {infectionType === 'ssi' && (
                          <div className="space-y-3 mt-3 pt-3 border-t">
                            <h5 className="font-medium text-sm text-gray-700">SSI Details</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="label text-xs">Type of SSI</label>
                                <select
                                  {...register(`infections.${index}.ssi.type`)}
                                  className="input text-sm"
                                  disabled={loading}
                                >
                                  <option value="">Select...</option>
                                  <option value="superficial">Superficial Incisional</option>
                                  <option value="deep">Deep Incisional</option>
                                  <option value="organSpace">Organ or Space</option>
                                </select>
                              </div>

                              <div>
                                <label className="label text-xs">Type of SSI Wound</label>
                                <select
                                  {...register(`infections.${index}.ssi.woundType`)}
                                  className="input text-sm"
                                  disabled={loading}
                                >
                                  <option value="">Select...</option>
                                  <option value="clean">Clean Wound</option>
                                  <option value="cleanContaminated">Clean-Contaminated Wound</option>
                                  <option value="contaminated">Contaminated Wound</option>
                                  <option value="dirtyInfected">Dirty/Infected Wound</option>
                                </select>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Pneumonia Specific Fields */}
                        {infectionType === 'pneumonia' && (
                          <div className="space-y-3 mt-3 pt-3 border-t">
                            <h5 className="font-medium text-sm text-gray-700">Pneumonia Type</h5>
                            <select
                              {...register(`infections.${index}.pneumoniaType`)}
                              className="input text-sm"
                              disabled={loading}
                            >
                              <option value="">Select...</option>
                              <option value="hapOther">Hospital Associated Pneumonia (other than VAP)</option>
                              <option value="vap">Ventilator Associated Pneumonia (VAP)</option>
                            </select>
                          </div>
                        )}

                        {/* BSI Specific Fields */}
                        {infectionType === 'bsi' && (
                          <div className="space-y-3 mt-3 pt-3 border-t">
                            <h5 className="font-medium text-sm text-gray-700">Blood Stream Infection Type</h5>
                            <select
                              {...register(`infections.${index}.bsiType`)}
                              className="input text-sm"
                              disabled={loading}
                            >
                              <option value="">Select...</option>
                              <option value="bsiOther">Blood Stream Infection (other than CLABSI)</option>
                              <option value="cabsi">Central Line Associated Blood Stream Infection (CLABSI)</option>
                            </select>
                          </div>
                        )}

                        {/* Other Infection Specify */}
                        {infectionType === 'other' && (
                          <div className="mt-3 pt-3 border-t">
                            <input
                              type="text"
                              placeholder="Specify infection type..."
                              {...register(`infections.${index}.otherSpecify`)}
                              className="input text-sm"
                              disabled={loading}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Patient Outcome */}
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-4">Patient Outcome</h4>

              <div className="space-y-3">
                <label className="flex items-center gap-3">
                  <input
                    type="radio"
                    value="admitted"
                    {...register('patientOutcome')}
                    disabled={loading}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Admitted</span>
                    <span className="block text-xs text-gray-600">Patient is still admitted / in care</span>
                  </span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="radio"
                    value="discharged"
                    {...register('patientOutcome')}
                    disabled={loading}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Discharged</span>
                    <div className="ml-6 mt-1">
                      <input
                        type="date"
                        placeholder="Date of Discharge"
                        {...register('outcomeDate')}
                        className="input text-sm"
                        disabled={loading || watch('patientOutcome') !== 'discharged'}
                      />
                    </div>
                  </span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="radio"
                    value="deceased"
                    {...register('patientOutcome')}
                    disabled={loading}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Deceased</span>
                    <div className="ml-6 mt-1">
                      <input
                        type="date"
                        placeholder="Date of Death"
                        {...register('outcomeDate')}
                        className="input text-sm"
                        disabled={loading || watch('patientOutcome') !== 'deceased'}
                      />
                    </div>
                  </span>
                </label>

                <label className="flex items-center gap-3">
                  <input
                    type="radio"
                    value="transferred"
                    {...register('patientOutcome')}
                    disabled={loading}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">
                    <span className="font-medium">Transferred to</span>
                    <div className="ml-6 mt-2 space-y-2">
                      <input
                        type="text"
                        placeholder="Facility name"
                        {...register('transferredToFacility')}
                        className="input text-sm"
                        disabled={loading || watch('patientOutcome') !== 'transferred'}
                      />
                      <input
                        type="date"
                        placeholder="Date of Transfer"
                        {...register('outcomeDate')}
                        className="input text-sm"
                        disabled={loading || watch('patientOutcome') !== 'transferred'}
                      />
                    </div>
                  </span>
                </label>
              </div>

              {errors.outcomeDate && <p className="text-sm text-danger-600 mt-2">{errors.outcomeDate.message}</p>}
              {errors.transferredToFacility && <p className="text-sm text-danger-600 mt-2">{errors.transferredToFacility.message}</p>}
            </div>

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-6 border-t">
              <button
                type="button"
                onClick={goToPreviousSection}
                disabled={currentSectionIndex === 0 || loading}
                className="btn btn-secondary flex-1"
              >
                ← Previous
              </button>
              <button
                type="button"
                onClick={goToNextSection}
                disabled={currentSectionIndex === sections.length - 1 || loading}
                className="btn btn-primary flex-1"
              >
                Next →
              </button>
            </div>
          </section>
        )}

        {/* REPORTING */}
        {activeSection === 'reporting' && (
          <section className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Reporting Details</h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="reportedBy" className="label">
                  Reported By
                </label>
                <input
                  id="reportedBy"
                  type="text"
                  {...register('reportedBy')}
                  className="input"
                  disabled={loading}
                  placeholder="Name of reporter"
                />
              </div>

              <div>
                <label htmlFor="reporterRank" className="label">
                  Rank/Position
                </label>
                <input
                  id="reporterRank"
                  type="text"
                  {...register('reporterRank')}
                  className="input"
                  disabled={loading}
                  placeholder="e.g., IPC Nurse, Doctor"
                />
              </div>

              <div>
                <label htmlFor="reportDate" className="label">
                  Report Date
                </label>
                <input
                  id="reportDate"
                  type="date"
                  {...register('reportDate')}
                  className="input"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900">
                <span className="font-semibold">Note:</span> Please ensure all required fields marked with <span className="text-danger-600">*</span> are completed before submitting.
              </p>
            </div>

            {/* Display validation errors */}
            {Object.keys(errors).length > 0 && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-6">
                <p className="text-sm font-semibold text-danger-900 mb-2">Validation Errors:</p>
                <ul className="text-sm text-danger-800 space-y-1">
                  {Object.entries(errors).map(([field, error]: any) => (
                    <li key={field}>• {field}: {error?.message || 'Invalid'}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Display error message */}
            {error && (
              <div className="bg-danger-50 border border-danger-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-danger-900">{error}</p>
              </div>
            )}

            {/* Form Actions - Only on Reporting Page */}
            <div className="space-y-3">
              {/* Navigation + Submit Row */}
              <div className="flex gap-3 pt-6 border-t">
                <button
                  type="button"
                  onClick={goToPreviousSection}
                  disabled={loading}
                  className="btn btn-secondary flex-1"
                >
                  ← Previous
                </button>
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : '💾 Submit HCAI Case'}
                </button>
              </div>
              {/* Clear Form Button */}
              <button
                type="button"
                onClick={() => reset()}
                className="btn btn-secondary w-full"
                disabled={loading}
              >
                Clear Form
              </button>
            </div>
          </section>
        )}
      </form>
    </div>
  );
}
